/**
 * Session storage for in-progress ADP pipeline configuration (sources → transforms → integrations → destinations).
 * Same-tab updates dispatch a custom event so open panels can refresh table/column options.
 */

import { useEffect, useMemo, useState } from 'react'

import type { DynamicField } from '@/features/adp/api/integrations.api'

export const ADP_PIPELINE_SESSION_KEY = 'akashic.adp.pipeline.v1'

export const ADP_PIPELINE_SESSION_EVENT = 'adp-pipeline-session-updated'

export type AdpAssetKind = 'source' | 'destination'

export type AdpAssetColumnEntry = {
  name: string
  dataType: string
  /** Included when saving the source file (selected columns). */
  selected?: boolean
  /** From data source configuration (business primary key). */
  isBusinessPrimaryKey?: boolean
  dqChecks?: string[]
}

export type AdpAssetSessionEntry = {
  nodeId: string
  assetKind: AdpAssetKind
  dataSourceName: string
  dataSourceInstance: string
  rooId?: number
  sourceDataSourceId?: number
  sourceFileId?: number
  targetDataSourceId?: number
  targetTableId?: number
  schemaName?: string
  tableName: string
  columns: AdpAssetColumnEntry[]
  updatedAt: number
  /** The session ID used for data profiling (if any). */
  profilingSessionId?: string
}

export type AdpSessionFormEntry = {
  connectorId: string
  form: Record<string, unknown>
  updatedAt: number
}

/** Synthetic output of a join/union step so the next “Add more” row can use `join_result:N` as a table. */
export type AdpIntegrationChainStep = {
  label: string
  columns: AdpAssetColumnEntry[]
}

export type AdpNodeRegisteredSourceSelection = {
  nodeId: string
  registeredSourceId: string
  registeredSourceName: string
  dataSourceName: string
  dataSourceInstance: string
  updatedAt: number
}

export type AdpSourceConfigDraft = {
  nodeId: string
  form: Record<string, string>
  dynamicConfigFields: Array<Record<string, unknown>>
  configurationOpen: boolean
  explorerOpen: boolean
  profilingOpen: boolean
  hideExplorerForRegisteredSelection?: boolean
  registeredAddNewRequested?: boolean
  testResponseFields: Array<Record<string, unknown>>
  responseSelections: Record<string, string>
  discoveredTables: string[]
  selectedTableName: string
  tableColumns: Array<Record<string, unknown>>
  columnConfigs: Record<string, unknown>
  updatedAt: number
}

export type AdpPipelineSessionV1 = {
  version: 1
  assets: Record<string, AdpAssetSessionEntry>
  transformations: Record<string, AdpSessionFormEntry>
  integrations: Record<string, AdpSessionFormEntry>
  /** Destination preview mapping rows: [sourceColumn, sourceType, destinationColumn, destinationType]. */
  columnMapping: Array<[string, string, string, string]>
  /** Final ordered operation JSON (source → destination) plus columnMapping. */
  fullOperationJson: Record<string, unknown>
  /** Per integration node: outputs for each completed join/union row (chained operations). */
  integrationChainOutputs: Record<string, AdpIntegrationChainStep[]>
  /** Store selected registered source per node for persistent configuration */
  selectedRegisteredSources: Record<string, AdpNodeRegisteredSourceSelection>
  /** Dynamic source-sheet UI/form draft keyed by canvas node id. */
  sourceConfigDrafts: Record<string, AdpSourceConfigDraft>
  /** The global session ID for the pipeline. */
  sessionId?: string
}

function emptySession(): AdpPipelineSessionV1 {
  return {
    version: 1,
    assets: {},
    transformations: {},
    integrations: {},
    columnMapping: [],
    fullOperationJson: {},
    integrationChainOutputs: {},
    selectedRegisteredSources: {},
    sourceConfigDrafts: {},
  }
}

export function readAdpPipelineSession(): AdpPipelineSessionV1 {
  if (globalThis.window === undefined) return emptySession()
  try {
    const raw = sessionStorage.getItem(ADP_PIPELINE_SESSION_KEY)
    if (!raw) return emptySession()
    const parsed = JSON.parse(raw) as Partial<AdpPipelineSessionV1>
    if (parsed?.version !== 1 || typeof parsed.assets !== 'object' || !parsed.assets) {
      return emptySession()
    }
    return {
      version: 1,
      assets: parsed.assets ?? {},
      transformations: parsed.transformations ?? {},
      integrations: parsed.integrations ?? {},
      columnMapping: Array.isArray(parsed.columnMapping)
        ? (parsed.columnMapping as Array<[string, string, string, string]>)
        : [],
      fullOperationJson:
        parsed.fullOperationJson && typeof parsed.fullOperationJson === 'object'
          ? (parsed.fullOperationJson as Record<string, unknown>)
          : {},
      integrationChainOutputs:
        typeof parsed.integrationChainOutputs === 'object' && parsed.integrationChainOutputs
          ? parsed.integrationChainOutputs
          : {},
      selectedRegisteredSources:
        typeof parsed.selectedRegisteredSources === 'object' && parsed.selectedRegisteredSources
          ? parsed.selectedRegisteredSources
          : {},
      sourceConfigDrafts:
        typeof parsed.sourceConfigDrafts === 'object' && parsed.sourceConfigDrafts
          ? parsed.sourceConfigDrafts
          : {},
    }
  } catch {
    return emptySession()
  }
}

function writeSession(next: AdpPipelineSessionV1) {
  sessionStorage.setItem(ADP_PIPELINE_SESSION_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent(ADP_PIPELINE_SESSION_EVENT))
}

export function replaceAdpPipelineSession(next: AdpPipelineSessionV1) {
  if (typeof window === 'undefined') return
  writeSession(next)
}

export function clearAdpPipelineSession() {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(ADP_PIPELINE_SESSION_KEY)
  window.dispatchEvent(new CustomEvent(ADP_PIPELINE_SESSION_EVENT))
}

export function pruneAdpPipelineSessionByNodeIds(nodeIds: string[]) {
  const keep = new Set(nodeIds)
  const s = readAdpPipelineSession()
  s.assets = Object.fromEntries(Object.entries(s.assets).filter(([nodeId]) => keep.has(nodeId)))
  s.transformations = Object.fromEntries(
    Object.entries(s.transformations).filter(([nodeId]) => keep.has(nodeId))
  )
  s.integrations = Object.fromEntries(
    Object.entries(s.integrations).filter(([nodeId]) => keep.has(nodeId))
  )
  s.integrationChainOutputs = Object.fromEntries(
    Object.entries(s.integrationChainOutputs).filter(([nodeId]) => keep.has(nodeId))
  )
  s.selectedRegisteredSources = Object.fromEntries(
    Object.entries(s.selectedRegisteredSources).filter(([nodeId]) => keep.has(nodeId))
  )
  s.sourceConfigDrafts = Object.fromEntries(
    Object.entries(s.sourceConfigDrafts).filter(([nodeId]) => keep.has(nodeId))
  )
  writeSession(s)
}

export function upsertAssetSession(entry: Omit<AdpAssetSessionEntry, 'updatedAt'>) {
  const s = readAdpPipelineSession()
  s.assets[entry.nodeId] = { ...entry, updatedAt: Date.now() }
  writeSession(s)
}

function columnNamesEqual(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

export type AdpGraphEdgeLite = { source: string; target: string }
export type AdpGraphNodeLite = { id: string; data?: unknown }

/**
 * When destination output preview is saved, mirror primary-key selection onto upstream
 * source session columns (and the source config draft, if any) using `columnMapping`.
 */
export function syncUpstreamSourcePrimaryKeyFromDestinationPreview(args: {
  destinationNodeId: string
  destinationFields: Array<{ fieldName: string; isPrimaryKey: boolean }>
  graphEdges: readonly AdpGraphEdgeLite[]
  graphNodes: readonly AdpGraphNodeLite[]
}): void {
  if (typeof globalThis.window === 'undefined') return
  const { destinationNodeId, destinationFields, graphEdges, graphNodes } = args
  if (!destinationNodeId.trim() || !destinationFields.length) return

  const s = readAdpPipelineSession()
  const mapping = s.columnMapping
  if (!Array.isArray(mapping) || mapping.length === 0) return

  const destPk = new Map<string, boolean>()
  for (const row of destinationFields) {
    const k = row.fieldName.trim().toLowerCase()
    if (k) destPk.set(k, Boolean(row.isPrimaryKey))
  }

  const incomingByTarget = new Map<string, string[]>()
  for (const edge of graphEdges) {
    const arr = incomingByTarget.get(edge.target) ?? []
    arr.push(edge.source)
    incomingByTarget.set(edge.target, arr)
  }
  const nodeById = new Map(graphNodes.map(n => [n.id, n]))

  const sourceNodeIds = new Set<string>()
  const stack: string[] = [destinationNodeId]
  const seen = new Set<string>()
  while (stack.length > 0) {
    const current = stack.pop()!
    if (seen.has(current)) continue
    seen.add(current)
    const parents = incomingByTarget.get(current) ?? []
    for (const p of parents) {
      const node = nodeById.get(p)
      const groupId = (node?.data as { groupId?: string } | undefined)?.groupId
      if (groupId === 'sources') sourceNodeIds.add(p)
      stack.push(p)
    }
  }

  if (sourceNodeIds.size === 0) return

  const resolvePkForSourceField = (sourceColumnName: string): boolean | undefined => {
    const mapRow = mapping.find(m => columnNamesEqual(m[0], sourceColumnName))
    if (!mapRow) return undefined
    const dest = mapRow[2]?.trim() ?? ''
    if (!dest) return undefined
    const k = dest.toLowerCase()
    if (!destPk.has(k)) return false
    return destPk.get(k) ?? false
  }

  const applyPkToSourceConfigs = (existing: Record<string, unknown> | undefined) => {
    if (!existing || typeof existing !== 'object') return null
    let changed = false
    const next: Record<string, unknown> = { ...existing }
    for (const [col, cfg] of Object.entries(next)) {
      if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) continue
      const pk = resolvePkForSourceField(col)
      if (pk === undefined) continue
      const o = cfg as Record<string, unknown>
      if (Boolean(o.isBusinessPrimaryKey) === pk) continue
      next[col] = { ...o, isBusinessPrimaryKey: pk }
      changed = true
    }
    return changed ? next : null
  }

  for (const sourceNodeId of sourceNodeIds) {
    const asset = s.assets[sourceNodeId]
    if (asset?.assetKind === 'source' && Array.isArray(asset.columns) && asset.columns.length > 0) {
      const nextColumns = asset.columns.map(col => {
        const pk = resolvePkForSourceField(col.name)
        if (pk === undefined) return col
        return { ...col, isBusinessPrimaryKey: pk }
      })
      s.assets[sourceNodeId] = { ...asset, columns: nextColumns, updatedAt: Date.now() }
    }

    const draft = s.sourceConfigDrafts[sourceNodeId]
    if (draft) {
      const nextCfg = applyPkToSourceConfigs(draft.columnConfigs as Record<string, unknown>)
      if (nextCfg) {
        s.sourceConfigDrafts[sourceNodeId] = { ...draft, columnConfigs: nextCfg, updatedAt: Date.now() }
      }
    }
  }

  writeSession(s)
}

export function upsertTransformationSession(
  nodeId: string,
  connectorId: string,
  form: Record<string, unknown>
) {
  const s = readAdpPipelineSession()
  s.transformations[nodeId] = { connectorId, form, updatedAt: Date.now() }
  writeSession(s)
}

export function upsertIntegrationSession(
  nodeId: string,
  connectorId: string,
  form: Record<string, unknown>
) {
  const s = readAdpPipelineSession()
  s.integrations[nodeId] = { connectorId, form, updatedAt: Date.now() }
  writeSession(s)
}

export function upsertColumnMappingSession(rows: Array<[string, string, string, string]>) {
  const s = readAdpPipelineSession()
  s.columnMapping = rows
  writeSession(s)
}

export function upsertFullOperationJsonSession(payload: Record<string, unknown>) {
  const s = readAdpPipelineSession()
  s.fullOperationJson = payload
  writeSession(s)
}

export function upsertPipelineSessionId(sessionId: string) {
  const s = readAdpPipelineSession()
  s.sessionId = sessionId
  writeSession(s)
}

export function upsertSelectedRegisteredSource(nodeId: string, registeredSource: {
  id: string
  name: string
  dataSourceName: string
  dataSourceInstance: string
}) {
  const s = readAdpPipelineSession()
  s.selectedRegisteredSources[nodeId] = {
    nodeId,
    registeredSourceId: registeredSource.id,
    registeredSourceName: registeredSource.name,
    dataSourceName: registeredSource.dataSourceName,
    dataSourceInstance: registeredSource.dataSourceInstance,
    updatedAt: Date.now(),
  }
  writeSession(s)
}

export function getSelectedRegisteredSource(nodeId: string): AdpNodeRegisteredSourceSelection | null {
  const s = readAdpPipelineSession()
  return s.selectedRegisteredSources[nodeId] || null
}

export function removeSelectedRegisteredSource(nodeId: string) {
  const s = readAdpPipelineSession()
  delete s.selectedRegisteredSources[nodeId]
  writeSession(s)
}

export function upsertAssetProfilingSessionId(nodeId: string, profilingSessionId: string) {
  const s = readAdpPipelineSession()
  if (s.assets[nodeId]) {
    s.assets[nodeId].profilingSessionId = profilingSessionId
    s.assets[nodeId].updatedAt = Date.now()
    writeSession(s)
  }
}

export function upsertSourceConfigDraft(
  nodeId: string,
  draft: Omit<AdpSourceConfigDraft, 'nodeId' | 'updatedAt'>
) {
  const s = readAdpPipelineSession()
  s.sourceConfigDrafts[nodeId] = {
    nodeId,
    ...draft,
    updatedAt: Date.now(),
  }
  writeSession(s)
}

export function getSourceConfigDraft(nodeId: string): AdpSourceConfigDraft | null {
  const s = readAdpPipelineSession()
  return s.sourceConfigDrafts[nodeId] ?? null
}

export function removeSourceConfigDraft(nodeId: string) {
  const s = readAdpPipelineSession()
  delete s.sourceConfigDrafts[nodeId]
  writeSession(s)
}

/** Value prefix for “previous step output” in join/union table pickers (`join_result:0`, …). */
export const JOIN_RESULT_VALUE_PREFIX = 'join_result:'

export function resolveAssetByTableValue(value: string): AdpAssetSessionEntry | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const assets = readAdpPipelineSession().assets
  if (trimmed.includes('::')) {
    const [nodeId, tableName] = trimmed.split('::', 2)
    const a = assets[nodeId]
    if (a && a.tableName === tableName) return a
    return null
  }
  for (const a of Object.values(assets)) {
    if (a.tableName === trimmed) return a
  }
  return null
}

export function resolveColumnsForPickerValue(
  value: string,
  _integrationNodeId: string,
  chainSoFar: AdpIntegrationChainStep[]
): AdpAssetColumnEntry[] | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.startsWith(JOIN_RESULT_VALUE_PREFIX)) {
    const idx = Number(trimmed.slice(JOIN_RESULT_VALUE_PREFIX.length))
    if (!Number.isNaN(idx)) return chainSoFar[idx]?.columns ?? null
  }
  return resolveAssetByTableValue(trimmed)?.columns ?? null
}

/** Resolve columns for a table picker value (asset or `join_result:N`). */
export function resolveColumnsForTableSelection(
  tableValue: string,
  integrationNodeId: string,
  chainSoFar: AdpIntegrationChainStep[]
): AdpAssetColumnEntry[] | null {
  return resolveColumnsForPickerValue(tableValue, integrationNodeId, chainSoFar)
}

/** Session column options for one selected table value. */
export function getSessionColumnOptionsForTableSelection(
  tableValue: string,
  integrationNodeId: string,
  chainSoFar: AdpIntegrationChainStep[]
): SessionOption[] {
  const cols = resolveColumnsForTableSelection(tableValue, integrationNodeId, chainSoFar) ?? []
  return columnsToSessionOptions(cols)
}

function columnsToSessionOptions(cols: AdpAssetColumnEntry[]): SessionOption[] {
  return cols.map(c => ({
    value: c.name,
    label:
      c.dataType && c.isBusinessPrimaryKey
        ? `${c.name} (${c.dataType}, PK)`
        : c.dataType
          ? `${c.name} (${c.dataType})`
          : c.isBusinessPrimaryKey
            ? `${c.name} (PK)`
            : c.name,
  }))
}

/** Prefer business-PK columns when present; otherwise all configured columns. */
function filterKeyColumnCandidates(cols: AdpAssetColumnEntry[]): AdpAssetColumnEntry[] {
  if (!cols.length) return []
  const pk = cols.filter(c => c.isBusinessPrimaryKey)
  return pk.length ? pk : cols
}

/** Common join types when the API does not supply options. */
export function getSessionJoinTypeOptions(): SessionOption[] {
  return [
    { value: 'inner', label: 'Inner join' },
    { value: 'left', label: 'Left join' },
    { value: 'right', label: 'Right join' },
    { value: 'outer', label: 'Outer join' },
  ]
}

export function mergeJoinColumns(a: AdpAssetColumnEntry[], b: AdpAssetColumnEntry[], joinType: string = 'left'): AdpAssetColumnEntry[] {
  const seen = new Set<string>()
  const out: AdpAssetColumnEntry[] = []
  
  const normalizedJoinType = joinType.toLowerCase()
  
  if (normalizedJoinType === 'inner') {
    // Inner join: include columns from both sides, avoid duplicates
    for (const col of [...a, ...b]) {
      const name = col.name
      if (seen.has(name)) continue
      seen.add(name)
      out.push({
        name,
        dataType: col.dataType,
        isBusinessPrimaryKey: col.isBusinessPrimaryKey,
        selected: col.selected,
        dqChecks: col.dqChecks,
      })
    }
  } else if (normalizedJoinType === 'left') {
    // Left join: all from left side + non-duplicate from right
    for (const col of a) {
      seen.add(col.name)
      out.push({
        name: col.name,
        dataType: col.dataType,
        isBusinessPrimaryKey: col.isBusinessPrimaryKey,
        selected: col.selected,
        dqChecks: col.dqChecks,
      })
    }
    for (const col of b) {
      if (seen.has(col.name)) continue
      seen.add(col.name)
      out.push({
        name: col.name,
        dataType: col.dataType,
        isBusinessPrimaryKey: col.isBusinessPrimaryKey,
        selected: col.selected,
        dqChecks: col.dqChecks,
      })
    }
  } else if (normalizedJoinType === 'right') {
    // Right join: all from right side + non-duplicate from left
    for (const col of b) {
      seen.add(col.name)
      out.push({
        name: col.name,
        dataType: col.dataType,
        isBusinessPrimaryKey: col.isBusinessPrimaryKey,
        selected: col.selected,
        dqChecks: col.dqChecks,
      })
    }
    for (const col of a) {
      if (seen.has(col.name)) continue
      seen.add(col.name)
      out.push({
        name: col.name,
        dataType: col.dataType,
        isBusinessPrimaryKey: col.isBusinessPrimaryKey,
        selected: col.selected,
        dqChecks: col.dqChecks,
      })
    }
  } else {
    // Full outer join or default: merge all, avoid duplicates
    for (const col of [...a, ...b]) {
      const name = col.name
      if (seen.has(name)) continue
      seen.add(name)
      out.push({
        name,
        dataType: col.dataType,
        isBusinessPrimaryKey: col.isBusinessPrimaryKey,
        selected: col.selected,
        dqChecks: col.dqChecks,
      })
    }
  }
  
  return out
}

function collectRepeatableGroups(fields: DynamicField[]): DynamicField[] {
  const out: DynamicField[] = []
  const walk = (list: DynamicField[]) => {
    for (const f of list) {
      if (f.type === 'group' && (f.multiple || f.repeatable) && f.fields?.length) out.push(f)
      if (f.type === 'group' && f.fields?.length) walk(f.fields)
    }
  }
  walk(fields)
  return out
}

/**
 * Derives synthetic step outputs from the current integration form (repeatable groups with table pickers).
 * Row N+1 can reference `join_result:N` as a table; resolution uses outputs from rows 0..N-1.
 */
export function computeIntegrationChainOutputs(
  integrationNodeId: string,
  connectorId: string,
  form: Record<string, unknown>,
  fields: DynamicField[]
): AdpIntegrationChainStep[] {
  const groups = collectRepeatableGroups(fields)
  if (groups.length === 0) return []

  const outputs: AdpIntegrationChainStep[] = []
  const group = groups[0]
  const rows = form[group.key]
  if (!Array.isArray(rows)) return []

  const subFields = group.fields ?? []

  rows.forEach((row, rowIndex) => {
    if (!row || typeof row !== 'object') return
    const r = row as Record<string, unknown>
    const tableLike = subFields.filter(f => integrationFieldLooksLikeTable(f))
    const tableValues = tableLike.map(f => String(r[f.key] ?? '').trim()).filter(Boolean)

    if (connectorId === 'join') {
      const left = tableValues[0]
      const right = tableValues[1]
      const colsLeft = left ? resolveColumnsForPickerValue(left, integrationNodeId, outputs) : null
      const colsRight = right ? resolveColumnsForPickerValue(right, integrationNodeId, outputs) : null
      if (colsLeft?.length && colsRight?.length) {
        // Extract join type from form row
        const joinType = Object.keys(r).find(key => /join.*type|join.*style|join.*mode/i.test(key))
        const joinTypeValue = joinType ? String(r[joinType] || '').trim() || 'left' : 'left'
        
        outputs[rowIndex] = {
          label: `Join result (step ${rowIndex + 1})`,
          columns: mergeJoinColumns(colsLeft, colsRight, joinTypeValue),
        }
      }
    } else if (connectorId === 'union') {
      const colMap = new Map<string, string>()
      for (const v of tableValues) {
        const cols = resolveColumnsForPickerValue(v, integrationNodeId, outputs) ?? []
        for (const c of cols) {
          if (!colMap.has(c.name)) colMap.set(c.name, c.dataType)
        }
      }
      const merged: AdpAssetColumnEntry[] = [...colMap.entries()].map(([name, dataType]) => ({
        name,
        dataType,
      }))
      if (merged.length) {
        outputs[rowIndex] = {
          label: `Union result (step ${rowIndex + 1})`,
          columns: merged,
        }
      }
    }
  })

  return outputs
}

export function persistIntegrationChainOutputs(
  integrationNodeId: string,
  steps: AdpIntegrationChainStep[]
) {
  const s = readAdpPipelineSession()
  s.integrationChainOutputs[integrationNodeId] = steps
  writeSession(s)
}

/**
 * Persists final integration output as a source-like session asset so downstream operations
 * (including union table pickers) can treat it like any other selectable table.
 */
export function upsertIntegrationOutputAssetSession(args: {
  outputNodeId: string
  connectorId: 'join' | 'union'
  integrationNodeId: string
  step: AdpIntegrationChainStep
}) {
  const s = readAdpPipelineSession()
  const tableName = `${args.connectorId}_result_${args.integrationNodeId}`
  s.assets[args.outputNodeId] = {
    nodeId: args.outputNodeId,
    assetKind: 'source',
    dataSourceName: args.step.label,
    dataSourceInstance: args.connectorId,
    tableName,
    columns: args.step.columns,
    updatedAt: Date.now(),
  }
  writeSession(s)
}

type SessionOption = { value: string; label: string }

function dedupeOptionsByValue(opts: SessionOption[]): SessionOption[] {
  const seen = new Set<string>()
  const out: SessionOption[] = []
  for (const o of opts) {
    if (seen.has(o.value)) continue
    seen.add(o.value)
    out.push(o)
  }
  return out
}

/**
 * Table names from all configured source/destination assets. When the same table name appears on
 * multiple nodes, values are qualified as `nodeId::tableName` and labels show the data source name.
 */
export function getSessionTableOptions(): SessionOption[] {
  const entries = Object.values(readAdpPipelineSession().assets)
  if (entries.length === 0) return []

  const counts = new Map<string, number>()
  for (const e of entries) {
    counts.set(e.tableName, (counts.get(e.tableName) ?? 0) + 1)
  }

  return entries.map(e => {
    const dup = (counts.get(e.tableName) ?? 0) > 1
    return {
      value: dup ? `${e.nodeId}::${e.tableName}` : e.tableName,
      label: dup ? `${e.dataSourceName} — ${e.tableName}` : e.tableName,
    }
  })
}

/** Qualified table value for a single asset (same rules as `getSessionTableOptions`). */
export function getQualifiedTableValueForAsset(a: AdpAssetSessionEntry): string {
  const entries = Object.values(readAdpPipelineSession().assets)
  const counts = new Map<string, number>()
  for (const e of entries) {
    counts.set(e.tableName, (counts.get(e.tableName) ?? 0) + 1)
  }
  const dup = (counts.get(a.tableName) ?? 0) > 1
  return dup ? `${a.nodeId}::${a.tableName}` : a.tableName
}

/**
 * Table dropdown for one **configured source node** on the canvas (primary or secondary path).
 * Empty if that source has not been saved to session yet.
 */
export function getTableOptionsForSourceNode(nodeId: string): SessionOption[] {
  const a = readAdpPipelineSession().assets[nodeId]
  if (!a) return []
  const value = getQualifiedTableValueForAsset(a)
  return [{ value, label: `${a.dataSourceName} — ${a.tableName}` }]
}

/** Column options scoped to one configured source node only. */
export function getColumnOptionsForSourceNode(nodeId: string, keysOnly = false): SessionOption[] {
  const a = readAdpPipelineSession().assets[nodeId]
  if (!a) return []
  const cols = keysOnly ? filterKeyColumnCandidates(a.columns) : a.columns
  return columnsToSessionOptions(cols)
}

/** Union of column names from session assets (for join/union/transform field pickers). */
export function getSessionColumnOptions(): SessionOption[] {
  const seen = new Set<string>()
  const out: SessionOption[] = []
  for (const e of Object.values(readAdpPipelineSession().assets)) {
    for (const c of e.columns) {
      if (seen.has(c.name)) continue
      seen.add(c.name)
      out.push({
        value: c.name,
        label: columnsToSessionOptions([c])[0]?.label ?? c.name,
      })
    }
  }
  return out
}

/** Columns from saved assets plus any integration join/union chain outputs (for downstream transforms). */
export function getExtendedColumnOptionsForPipeline(): SessionOption[] {
  const seen = new Set<string>()
  const out: SessionOption[] = []
  const s = readAdpPipelineSession()
  for (const e of Object.values(s.assets)) {
    for (const c of e.columns) {
      if (seen.has(c.name)) continue
      seen.add(c.name)
      out.push({
        value: c.name,
        label: columnsToSessionOptions([c])[0]?.label ?? c.name,
      })
    }
  }
  for (const steps of Object.values(s.integrationChainOutputs ?? {})) {
    for (const step of steps) {
      for (const c of step.columns) {
        if (seen.has(c.name)) continue
        seen.add(c.name)
        out.push({
          value: c.name,
          label: columnsToSessionOptions([c])[0]?.label ?? c.name,
        })
      }
    }
  }
  return out
}

/** Structural match for integration + transformation dynamic fields */
type SessionDynamicField = {
  type: string
  key: string
  label: string
  dataSource?: string
  options?: { value: string; label: string }[]
  fields?: SessionDynamicField[]
}

export function integrationFieldLooksLikeTable(field: SessionDynamicField): boolean {
  const t = `${field.key} ${field.label}`.toLowerCase()
  if (/\b(column|columns|field key|join key|source key|target key|primary key|secondary key|foreign key|key)\b/i.test(t) && !/\btable\b/i.test(t)) return false
  return (
    /\b(table|tables)\b/i.test(t) ||
    /\bsource(s)?\b/i.test(t) ||
    /\b(source file|file name)\b/i.test(t) ||
    /\b(left|right)\s*[_]?\s*table\b/i.test(t) ||
    /\b(primary|secondary)\s*table\b/i.test(t)
  )
}

export function integrationFieldLooksLikePrimaryTable(field: SessionDynamicField): boolean {
  if (!integrationFieldLooksLikeTable(field)) return false
  const t = `${field.key} ${field.label}`.toLowerCase()
  return /\b(primary|left|first|source)\b/i.test(t)
}

export function integrationFieldLooksLikeSecondaryTable(field: SessionDynamicField): boolean {
  if (!integrationFieldLooksLikeTable(field)) return false
  const t = `${field.key} ${field.label}`.toLowerCase()
  return /\b(secondary|right|target)\b/i.test(t)
}

export function integrationFieldLooksLikeJoinType(field: SessionDynamicField): boolean {
  const t = `${field.key} ${field.label}`.toLowerCase()
  if (integrationFieldLooksLikeTable(field)) return false
  return /\b(join\s*type|join\s*style|join\s*mode)\b/i.test(t)
}

export function integrationFieldLooksLikePrimaryKeyColumn(field: SessionDynamicField): boolean {
  const t = `${field.key} ${field.label}`.toLowerCase()
  if (integrationFieldLooksLikeTable(field)) return false
  return /\b(primary\s*table\s*key|primary\s*key|left\s*key|source\s*key|left\s*column|primary\s*column)\b/i.test(
    t
  )
}

export function integrationFieldLooksLikeSecondaryKeyColumn(field: SessionDynamicField): boolean {
  const t = `${field.key} ${field.label}`.toLowerCase()
  if (integrationFieldLooksLikeTable(field)) return false
  return /\b(secondary\s*key|right\s*key|secondary\s*column|foreign\s*key|target\s*key|right\s*column|secondary\s*table\s*key)\b/i.test(
    t
  )
}

export function integrationFieldLooksLikeColumn(field: SessionDynamicField): boolean {
  const t = `${field.key} ${field.label}`.toLowerCase()
  if (integrationFieldLooksLikeTable(field)) return false
  if (integrationFieldLooksLikeJoinType(field)) return false
  if (integrationFieldLooksLikePrimaryKeyColumn(field)) return false
  if (integrationFieldLooksLikeSecondaryKeyColumn(field)) return false
  return /\b(column|columns|field|fields|schema|attribute|group\s*by|order\s*by|partition\s*by|sort\s*by)\b/i.test(t)
}

/**
 * Table dropdown options for one row in a join/union repeatable group: session assets plus
 * `join_result:N` when rowIndex > 0 and the previous step produced an output.
 */
export function getTableOptionsForIntegrationRow(
  rowIndex: number,
  chainSteps: AdpIntegrationChainStep[]
): SessionOption[] {
  const base = getSessionTableOptions()
  if (rowIndex > 0) {
    const prev = chainSteps[rowIndex - 1]
    if (prev?.columns?.length) {
      return dedupeOptionsByValue([
        {
          value: `${JOIN_RESULT_VALUE_PREFIX}${rowIndex - 1}`,
          label: prev.label,
        },
        ...base,
      ])
    }
  }
  return base
}

/**
 * Table options when a canvas source node is wired to the join/union: that source’s table plus
 * optional `join_result` for chained rows.
 */
export function getTableOptionsForLinkedSourceRow(
  sourceNodeId: string | undefined,
  rowIndex: number,
  chainSteps: AdpIntegrationChainStep[]
): SessionOption[] {
  const fromNode = sourceNodeId ? getTableOptionsForSourceNode(sourceNodeId) : []
  if (rowIndex > 0) {
    const prev = chainSteps[rowIndex - 1]
    if (prev?.columns?.length) {
      return dedupeOptionsByValue([
        {
          value: `${JOIN_RESULT_VALUE_PREFIX}${rowIndex - 1}`,
          label: prev.label,
        },
        ...fromNode,
      ])
    }
  }
  return fromNode
}

/** Column options scoped to the tables selected in the same group row (fallback: extended pipeline columns). */
export function getColumnOptionsForIntegrationRow(
  row: Record<string, unknown>,
  subFields: DynamicField[],
  integrationNodeId: string,
  chainSteps: AdpIntegrationChainStep[]
): SessionOption[] {
  const tableLike = subFields.filter(f => integrationFieldLooksLikeTable(f))
  const vals = tableLike.map(f => String(row[f.key] ?? '').trim()).filter(Boolean)
  const seen = new Set<string>()
  const out: SessionOption[] = []
  for (const v of vals) {
    const cols = resolveColumnsForPickerValue(v, integrationNodeId, chainSteps) ?? []
    for (const c of cols) {
      if (seen.has(c.name)) continue
      seen.add(c.name)
      out.push({
        value: c.name,
        label: columnsToSessionOptions([c])[0]?.label ?? c.name,
      })
    }
  }
  if (out.length === 0) return getExtendedColumnOptionsForPipeline()
  return out
}

/**
 * Per-field column/join options inside a repeatable row (primary/secondary keys, join type).
 */
export function getColumnOptionsForIntegrationField(
  row: Record<string, unknown>,
  subFields: DynamicField[],
  field: DynamicField,
  integrationNodeId: string,
  chainSteps: AdpIntegrationChainStep[],
  upstreamSourceNodeIds?: string[] | null
): SessionOption[] {
  const tableLike = subFields.filter(f => integrationFieldLooksLikeTable(f))
  const primaryTableVal = tableLike[0]
    ? String(row[tableLike[0].key] ?? '').trim()
    : ''
  const secondaryTableVal = tableLike[1]
    ? String(row[tableLike[1].key] ?? '').trim()
    : ''

  if (integrationFieldLooksLikeJoinType(field)) {
    return getSessionJoinTypeOptions()
  }

  if (integrationFieldLooksLikePrimaryKeyColumn(field)) {
    let cols = resolveColumnsForTableSelection(primaryTableVal, integrationNodeId, chainSteps)
    if (!cols?.length && upstreamSourceNodeIds?.[0]) {
      return getColumnOptionsForSourceNode(upstreamSourceNodeIds[0], false)
    }
    return columnsToSessionOptions(cols ?? [])
  }

  if (integrationFieldLooksLikeSecondaryKeyColumn(field)) {
    let cols = resolveColumnsForTableSelection(secondaryTableVal, integrationNodeId, chainSteps)
    if (!cols?.length && upstreamSourceNodeIds?.[1]) {
      return getColumnOptionsForSourceNode(upstreamSourceNodeIds[1], false)
    }
    return columnsToSessionOptions(cols ?? [])
  }

  return getColumnOptionsForIntegrationRow(row, subFields, integrationNodeId, chainSteps)
}

function toDynamicOptions(opts: SessionOption[]): { value: string; label: string }[] {
  return opts.map(o => ({ value: o.value, label: o.label }))
}

function mergeOptions(
  apiOpts: { value: string; label: string }[] | undefined,
  sessionOpts: { value: string; label: string }[]
): { value: string; label: string }[] {
  const fromApi = apiOpts?.length ? apiOpts : []
  return dedupeOptionsByValue([
    ...fromApi.map(o => ({ value: o.value, label: o.label })),
    ...sessionOpts.map(o => ({ value: o.value, label: o.label })),
  ]).map(o => ({ value: o.value, label: o.label }))
}

/**
 * Merges API-driven field options with session-derived table/column lists for select/multiselect fields.
 */
export function applySessionOptionsToFields<T extends SessionDynamicField>(
  fields: T[],
  tableOpts: SessionOption[],
  columnOpts: SessionOption[]
): T[] {
  const tDyn = toDynamicOptions(tableOpts)
  const cDyn = toDynamicOptions(columnOpts)
  const jDyn = toDynamicOptions(getSessionJoinTypeOptions())

  const walk = (list: T[]): T[] =>
    list.map(field => {
      if (field.type === 'group' && field.fields?.length) {
        return { ...field, fields: walk(field.fields as T[]) } as T
      }
      if (field.type === 'select' || field.type === 'multiselect') {
        // Handle API-driven data sources first (new approach)
        if (field.dataSource) {
          switch (field.dataSource) {
            case 'tables_list':
              return { ...field, options: mergeOptions(field.options, tDyn) } as T
            case 'columns_based_on_table':
              return { ...field, options: mergeOptions(field.options, cDyn) } as T
            case 'join_type_list':
              return { ...field, options: mergeOptions(field.options, jDyn) } as T
            case 'data_types_list':
              // Keep existing options for data types
              return field
            default:
              // For unknown data sources, keep existing options
              return field
          }
        }
        
        // If field already has options (from API schema), preserve them
        if (field.options?.length) {
          return field
        }
        
        // Fallback to legacy pattern matching (old approach)
        if (integrationFieldLooksLikeTable(field)) {
          return { ...field, options: mergeOptions(field.options, tDyn) } as T
        }
        if (integrationFieldLooksLikeJoinType(field)) {
          return { ...field, options: mergeOptions(field.options, jDyn) } as T
        }
        if (
          integrationFieldLooksLikePrimaryKeyColumn(field) ||
          integrationFieldLooksLikeSecondaryKeyColumn(field) ||
          integrationFieldLooksLikeColumn(field)
        ) {
          return { ...field, options: mergeOptions(field.options, cDyn) } as T
        }
      }
      return field
    })

  return walk(fields)
}

export function useAdpPipelineSessionVersion(): number {
  const [v, setV] = useState(0)
  useEffect(() => {
    const on = () => setV(x => x + 1)
    window.addEventListener(ADP_PIPELINE_SESSION_EVENT, on)
    return () => window.removeEventListener(ADP_PIPELINE_SESSION_EVENT, on)
  }, [])
  return v
}

export function useAdpPipelineSessionSnapshot(): AdpPipelineSessionV1 {
  const v = useAdpPipelineSessionVersion()
  return useMemo(() => readAdpPipelineSession(), [v])
}

export function mergeSavedForm<T extends Record<string, unknown>>(
  initial: T,
  saved: Record<string, unknown> | undefined
): T {
  if (!saved) return initial
  const out = { ...initial } as Record<string, unknown>
  for (const key of Object.keys(saved)) {
    if (saved[key] !== undefined) out[key] = saved[key]
  }
  return out as T
}
